import type { Role, ResellerTier } from '../types/profile'
import { CLIENTS_HUB_ENTRY_ID, getSolutionAdminEntries, getSolutionNavSections } from './solutionRegistry'

export type IconName =
  | 'grid'
  | 'bars'
  | 'users'
  | 'bell'
  | 'briefcase'
  | 'shield'
  | 'gear'
  | 'calendar'
  | 'chart-line'
  | 'help'
  | 'sliders'
  | 'file'
  | 'wallet'

/** Spec 161 (D-161-07) — onglet d'une entrée : une page existante, à sa propre adresse, réunie sous l'entrée. */
export interface NavOnglet {
  id: string
  label: string
  path: string
}

export interface NavItem {
  id: string
  label: string
  path: string
  icon?: IconName
  badge?: string
  badgeType?: 'alert' | 'v2' | 'neutral'
  disabled?: boolean
  /**
   * Spec 161 — pages réunies sous l'entrée, affichées en onglets au-dessus du contenu. L'entrée COUVRE son chemin et
   * celui de chaque onglet (entrée active, titre d'onglet, télémétrie, changement de client, date d'audit de la barre du
   * haut) : une page regroupée qu'aucune entrée ne couvrirait perdrait les cinq (garde-fou B2).
   */
  onglets?: NavOnglet[]
}

export interface NavSection {
  id: string
  /** Absent : section sans titre, rendue sans en-tête ni repli (spec 161, section d'accueil). */
  label?: string
  icon: IconName
  solutionCode?: string
  defaultOpen?: boolean
  items: NavItem[]
}

export interface NavStructure {
  sections: NavSection[]
  bottomItems: NavItem[]
}

function couvreChemin(chemin: string, pathname: string): boolean {
  return pathname === chemin || pathname.startsWith(`${chemin}/`)
}

/** Les chemins qu'une entrée couvre : le sien, puis ceux de ses onglets. */
export function cheminsCouverts(item: NavItem): string[] {
  return [item.path, ...(item.onglets ?? []).map(o => o.path)]
}

/** Spec 161 — l'entrée couvre-t-elle cette page ? Par segment entier : `/licences` ne couvre pas `/licences-utilisateurs`. */
export function itemCouvre(item: NavItem, pathname: string): boolean {
  return cheminsCouverts(item).some(c => couvreChemin(c, pathname))
}

// Spec 161 (D-161-06) — section d'accueil de la plateforme, SANS titre : elle remplace la section « Tableau de bord »
// (« Vue d'ensemble », « Alertes », « Usages »). Pas de solutionCode : le Tableau de bord affiche les sections que les
// solutions déclarent, et Alertes est une page de la plateforme. « Usages » (grisée, sans donnée) est retirée (E1,
// accepté par Christophe le 2026-10-12).
const accueilSection: NavSection = {
  id: 'accueil',
  icon: 'grid',
  defaultOpen: true,
  items: [
    { id: 'tableau-de-bord', label: 'Tableau de bord', path: '/overview', icon: 'grid' },
    { id: 'alertes', label: 'Alertes', path: '/alertes', icon: 'bell' },
  ],
}

// Spec 161 — bas de menu, identique pour tous les rôles. « Paramètres » garde l'identifiant `parametrages` ; « Aide »
// y entre et reste aussi dans la barre du haut.
const bottomItems = (): NavItem[] => [
  { id: 'parametrages', label: 'Paramètres', path: '/settings', icon: 'gear' },
  { id: 'aide', label: 'Aide', path: '/aide', icon: 'help' },
]

// Spec 133 — la section « FinOps 365 » vivait ici en dur. Elle est desormais DECLAREE par la
// solution elle-meme (`solutions/finops/register.tsx`) et agregee ici sans etre nommee : le socle
// connait le concept de solution, pas les solutions. Ajouter une solution ne touche plus ce
// fichier (FR-005, FR-006).
const solutionSections = (): NavSection[] => getSolutionNavSections()

const clientsSection: NavSection = {
  id: 'clients',
  label: 'Clients',
  icon: 'users',
  defaultOpen: true,
  items: [
    { id: 'portfolio', label: 'Portefeuille', path: '/portfolio' },
  ],
}

// Spec 084 — section « Clients » du revendeur : Portefeuille + (selon le niveau) Administration
// revendeur. L'entrée Administration est MASQUÉE au Chargé de comptes (AccountAgent) : seuls
// l'Admin revendeur et le Responsable de comptes y accèdent (gating par resellerTier).
function resellerClientsSection(resellerTier: ResellerTier | null | undefined): NavSection {
  const items: NavItem[] = [
    { id: 'portfolio', label: 'Portefeuille', path: '/portfolio' },
    // Spec 089 — vue Onboarding (clients en cours de mise en service) ouverte à tous les profils revendeur.
    { id: 'onboarding', label: 'Onboarding', path: '/reseller/onboarding' },
    // Spec 111 — vue Récurrence (cohortes des clients récurrents du périmètre), tous profils revendeur.
    { id: 'reseller-recurrence', label: 'Récurrence', path: '/reseller/recurrence' },
  ]
  if (resellerTier === 'ResellerAdmin' || resellerTier === 'AccountManager') {
    items.push({ id: 'reseller-admin', label: 'Administration revendeur', path: '/reseller/admin' })
  }
  return { id: 'clients', label: 'Clients', icon: 'users', defaultOpen: true, items }
}

// Spec 113 — menu « Administration » (Admin Pragmelis uniquement) : 5 domaines, chacun = une page-hub.
// Remplace l'ancienne entrée unique bottomItem `/admin` (page monolithe à 11 onglets).
// Spec 148 — suivi des écrans d'administration DÉCLARÉS par les solutions (`adminEntries`), agrégés sans
// être nommés. Une fonction et non une constante : les solutions s'inscrivent après le chargement de ce module.
const adminSection = (): NavSection => ({
  id: 'administration',
  label: 'Administration',
  icon: 'shield',
  defaultOpen: false,
  items: [
    { id: CLIENTS_HUB_ENTRY_ID, label: 'Clients', path: '/admin/clients' },
    { id: 'admin-revendeurs', label: 'Revendeurs', path: '/admin/revendeurs' },
    { id: 'admin-finops', label: 'FinOps', path: '/admin/finops' },
    { id: 'admin-notifications', label: 'Notifications', path: '/admin/notifications' },
    { id: 'admin-utilisateurs', label: 'Utilisateurs', path: '/admin/utilisateurs' },
    ...getSolutionAdminEntries(),
  ],
})

export function getNavStructure(role: Role, resellerTier?: ResellerTier | null): NavStructure {
  switch (role) {
    case 'Admin':
      return {
        // Spec 091 — section « Clients » après les sections de solution ; Spec 113 — section « Administration ».
        sections: [accueilSection, ...solutionSections(), clientsSection, adminSection()],
        bottomItems: bottomItems(),
      }
    case 'Consultant':
      return {
        // Spec 091 — section « Clients » placée après les sections de solution
        sections: [accueilSection, ...solutionSections(), clientsSection],
        bottomItems: bottomItems(),
      }
    case 'Reseller':
      return {
        // Spec 161 (D-161-06) — le revendeur arrive sur son Portefeuille : la section « Clients » vient EN TÊTE, puis
        // l'accueil et les sections de solution, qui portent sur le client affiché.
        sections: [resellerClientsSection(resellerTier), accueilSection, ...solutionSections()],
        bottomItems: bottomItems(),
      }
    case 'Client':
      return {
        sections: [accueilSection, ...solutionSections()],
        bottomItems: bottomItems(),
      }
  }
}
